// ── Activity-date streak sync ────────────────────────────────────────────────
// The watch streak (feeds streak_3/7/30/100 achievements + the Trophy Case /
// MarqueeEngine cinematic) was purely localStorage: useWatchlist.jsx wrote
// nextep_activity_dates on every first-watch-of-the-day, MarqueeEngine.jsx and
// App.jsx's achievement check both read it back to compute the current streak —
// nothing ever synced it to the account. Switching devices (or logging back in
// on a cleared cache) made a genuinely-daily watcher look like they'd broken
// their streak, since each device only ever saw its own local date list.
//
// Same shape as coachmarks/seenVersion/checkinSnooze: localStorage first
// (instant — the hot mark-watched path never waits on a round trip),
// profiles.ui_prefs.activity_dates as the durable source of truth, synced in
// once on profile load.
import { safeStorage } from './safeStorage'
import { supabase } from './supabase'

const KEY = 'nextep_activity_dates'
const CAP = 90

export function getActivityDates() {
  try { return JSON.parse(safeStorage.get(KEY) || '[]') } catch { return [] }
}

// Records today's date (idempotent — a no-op if already recorded) and returns
// the updated list. Mirrors the exact dedupe+cap logic that used to be inlined
// in useWatchlist.jsx's saveProgress.
export function recordActivityToday(today) {
  const dates = getActivityDates()
  if (dates.includes(today)) return dates
  const next = [today, ...dates].slice(0, CAP)
  try { safeStorage.set(KEY, JSON.stringify(next)) } catch { /* non-fatal */ }
  // Fire-and-forget atomic merge server-side — see mark_activity_date_rpc.sql.
  try { supabase.rpc('mark_activity_date', { p_date: today }).then(() => {}, () => {}) } catch { /* non-fatal */ }
  return next
}

// Called once when the profile loads (useAuth.jsx) — merges the account's
// server-side date list into this device's localStorage (union, capped to the
// most recent 90), so a fresh login doesn't show a broken/reset streak.
export function syncActivityDatesFromProfile(profile) {
  const remote = profile?.ui_prefs?.activity_dates
  if (!Array.isArray(remote) || !remote.length) return
  const local = getActivityDates()
  const merged = [...new Set([...remote, ...local])].sort((a, b) => (a < b ? 1 : -1)).slice(0, CAP)
  try { safeStorage.set(KEY, JSON.stringify(merged)) } catch { /* non-fatal */ }
}
